<script lang="ts">
	// A1: the admin's front door. One button, Google, and only the accounts on
	// Bosla's admin list get in. A refused account is shown its own Google id,
	// whole, with Copy, because adding that id to the list in Pulumi is the
	// only way in and the refused person is the one who knows it is theirs.
	//
	// The button leaves this origin: sign-in is an OAuth redirect, not a fetch.

	import Band from '../components/Band.svelte';
	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import { apiBase } from '../api/client';
	import { copy } from './copy';
	import type { Notice } from './session.svelte';

	interface Props {
		notice: Notice;
		googleId: string;
	}

	let { notice, googleId }: Props = $props();

	let leaving = $state(false);
	let copied = $state(false);
	let copiedTimer: ReturnType<typeof setTimeout> | undefined;

	function signIn() {
		leaving = true;
		window.location.assign(`${apiBase}/auth/google/admin`);
	}

	async function copyId() {
		try {
			await navigator.clipboard.writeText(googleId);
		} catch {
			// The id stays on screen to select by hand.
			return;
		}
		copied = true;
		clearTimeout(copiedTimer);
		copiedTimer = setTimeout(() => (copied = false), 2000);
	}
</script>

<section class="screen" data-testid="admin-signin">
	<div class="card">
		<BrandMark size={26} />
		<div class="words">
			<h1>{copy.title}</h1>
			<p class="line">{copy.signInLine}</p>
		</div>
		{#if notice === 'ended'}
			<Band tone="flag" testId="admin-signin-ended">{copy.sessionEnded}</Band>
		{:else if notice === 'unfinished'}
			<Band tone="flag" testId="admin-signin-unfinished">{copy.signInUnfinished}</Band>
		{:else if notice === 'refused'}
			<div class="refused">
				<Band tone="flag" testId="admin-signin-refused">{copy.refused}</Band>
				{#if googleId}
					<div class="id">
						<div class="field">
							<Field testId="admin-google-id" label={copy.googleId} value={googleId} mono />
						</div>
						<Button
							testId="admin-copy-id"
							label={copy.copy}
							tone="secondary"
							size="md"
							state={copied ? 'done' : 'idle'}
							doneLabel={copy.copied}
							onPress={() => void copyId()}
						/>
					</div>
					<p class="how">{copy.refusedHow}</p>
				{/if}
			</div>
		{/if}
		<Button
			testId="admin-signin-google"
			label={copy.signIn}
			google
			wide
			state={leaving ? 'pending' : 'idle'}
			pendingLabel={copy.signInOpening}
			onPress={signIn}
		/>
	</div>
</section>

<style>
	.screen {
		min-block-size: 100dvh;
		display: grid;
		place-items: center;
		padding: var(--space-9);
		background: var(--surface-page);
	}

	.card {
		inline-size: 100%;
		max-inline-size: 440px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
		padding: var(--card-pad);
	}

	.words {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
	}

	.line {
		margin: 0;
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.refused {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.id {
		display: flex;
		align-items: flex-end;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	.field {
		flex: 1 1 220px;
		min-inline-size: 0;
		overflow-wrap: anywhere;
	}

	.how {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}
</style>
